import React, { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useLocation, useNavigate } from "react-router-dom";
import { SxProps, Theme } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import helperMethods from "../../helper/helperMethods";
import AutoComplete from "../mui/AutoComplete";
import { useGetTargetListQuery } from "../../store/apis/targetApis";
import Dropdown from "../mui/Dropdown";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { acl } from "../../helper/userAccessConstant";
import usePermission from "../../helper/usePermission";
import { useAppSelector } from "../../hooks/reduxHooks";

const tabIndicatorStyles: SxProps<Theme> = {
  bgcolor: "rgb(89, 89, 89)",
  height: "2px",
};

const NavigationBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const filter = useSelector((state: RootState) => state?.user?.filter);

  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [params, setParams] = useState({
    programMasterId: filter?.selectedProgram?.value || undefined,
  });

  const scopesStore = useAppSelector((state: RootState) => state?.user?.scopes);

  const { permissions = {}, isAdmin } = usePermission();

  const permissionKeys = [
    acl.dashboard.module,
    acl.leaderboard.module,
    acl.configuration.module,
    acl.target_achievements.module,
    acl.data_input.module,
    acl.user_management.module,
    acl.reports.module,
    acl.centres.module,
  ];

  const permissionsMap = permissionKeys.reduce((acc, key) => {
    acc[key] = permissions[key]?.module?.canRead;
    return acc;
  }, {});

  const {
    dashboard,
    leaderboard,
    configuration,
    target_achievements,
    data_input,
    user_management,
    reports,
    centres,
  } = permissionsMap;

  const isTargetOptionAllowed: any = permissions?.target_achievements;

  useEffect(() => {
    setParams({
      programMasterId: filter?.selectedProgram?.value || undefined,
    });
  }, [filter, scopesStore]);

  // API call
  const { data: targetOptions } = useGetTargetListQuery(params, {
    skip: !params?.programMasterId, // Skip API call if programMasterId is missing
  });

  const targetAchievementOptions = (responseArray: any) => {
    if (!responseArray?.length) return [];

    if (isAdmin) {
      return responseArray;
    }

    // // Step 1: Get the filtered submodules (keys with canRead === true)
    // const allowedSubmodules = isTargetOptionAllowed
    //   ? Object.keys(isTargetOptionAllowed).filter(
    //       (key) => key !== "module" && isTargetOptionAllowed[key]?.canRead
    //     )
    //   : [];

    // // Step 2: Filter the response array
    // const filteredArray = responseArray.filter((item: any) => {
    //   return item?.tabDetails?.some((tab: any) =>
    //     allowedSubmodules?.includes(tab?.tabId)
    //   );
    // });

    // // Step 3: Return the filtered array
    // return filteredArray.map((item: any) => {
    //   return {
    //     ...item,
    //     tabDetails: item.tabDetails.filter((tab: any) =>
    //       allowedSubmodules.includes(tab.tabId)
    //     ),
    //   };
    // });

    const allowedSubmodules = new Set(
      Object.keys(isTargetOptionAllowed || {}).filter(
        (key) => key !== "module" && isTargetOptionAllowed[key]?.canRead
      )
    );


    // Return early if no allowed modules
    if (!allowedSubmodules.size) return [];

    // Single pass filter and map
    return responseArray
      .filter((item: any) =>
        item?.tabDetails?.some((tab: any) => allowedSubmodules.has(tab?.tabId))
      )
      .map((item: any) => ({
        ...item,
        tabDetails: item.tabDetails.filter((tab: any) =>
          allowedSubmodules.has(tab.tabId)
        ),
      }));
  };

  const targetAchivementOptionsList = [
    ...(targetOptions?.allTabs?.map((option: any) => option) || []), // Safeguard against undefined `allTabs`
    {
      categories: "Target Achivement",
      tabDetails: [{ tabId: "target_achievement_submodule" }],
    },
  ];

  const isDataInputOptionAllowed: any = permissions?.data_input;

  const dataInputOptionsList = (responseArray: any) => {
    if (!responseArray) return [];

    if (isAdmin) {
      return responseArray;
    }
    // Step 1: Get the filtered submodules (keys with canRead === true)
    const allowedSubmodules = isDataInputOptionAllowed
      ? Object.keys(isDataInputOptionAllowed).filter(
          (key) => key !== "module" && isDataInputOptionAllowed[key]?.canRead
        )
      : [];

    // Step 2: Filter the response array
    const filteredArray = responseArray.filter((item: any) => {
      return item?.tabDetails?.some((tab: any) =>
        allowedSubmodules.includes(tab.tabId)
      );
    });

    // Step 3: Return the filtered array
    return filteredArray.map((item: any) => {
      return {
        ...item,
        tabDetails: item.tabDetails.filter((tab: any) =>
          allowedSubmodules.includes(tab.tabId)
        ),
      };
    });
  };

  const userManagementTabs = [
    { label: "User Management", path: "/user-management", canRead: true },
    { label: "Centres", path: "/centres", canRead: true },
  ];

  let firstLayerTabs: any = [
    { label: "Dashboard", path: "/dashboard", canRead: dashboard },
    { label: "Leaderboard", path: "/leaderboard", canRead: leaderboard },
    {
      label: "Configuration",
      path: "/configuration",
      canRead: configuration,
    },
    {
      label: "Target achievement",
      path: "/target-achievement",
      options: true,
      optionList: targetAchievementOptions(targetAchivementOptionsList),
      canRead: target_achievements,
    },
    {
      label: "Data Input",
      path: "/data-input",
      options: true,
      optionList: dataInputOptionsList(targetOptions?.allTabs),
      canRead: data_input,
    },
    {
      label: "User Management",
      path: "/user-management",
      canRead: user_management,
    },
    { label: "Reports", path: "/reports", canRead: reports },
    { label: "Centres", path: "/centres", canRead: centres },
  ].filter((tab: any) => {
    const isDataInputEmpty =
      tab.label === "Data Input" && tab.optionList?.length === 0;
    const isTargetAchievementEmpty =
      tab.label === "Target Achivement" && tab.optionList?.length === 0;

    if (isDataInputEmpty || isTargetAchievementEmpty) {
      return false;
    }

    return tab.canRead;
  });

  const isUserManagemnt = helperMethods.isUserManagemnt();
  let tabs: any = [];
  if (isUserManagemnt) {
    tabs = userManagementTabs;
  } else {
    tabs = firstLayerTabs;
  }

  const selectedIndex = tabs.findIndex((tab: any) =>
    currentPath.includes(tab.path)
  );

  const [value, setValue] = React.useState(
    selectedIndex !== -1 ? selectedIndex : 0
  );
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedOption, setSelectedOption] = React.useState<any>(null);

  React.useEffect(() => {
    setSelectedOption(targetOptions?.allTabs[0]);
  }, [targetOptions]);

  React.useEffect(() => {
    if (isUserManagemnt) {
      const pathName = location.pathname;
      const tabIndex = userManagementTabs.findIndex(
        (tab) => tab.path === pathName
      );
      if (tabIndex !== -1) {
        setValue(tabIndex);
      }else{
        navigate(userManagementTabs[0].path);
      }
    } else {
      setValue(selectedIndex !== -1 ? selectedIndex : 0);
    }
  }, [currentPath, selectedIndex, location]);

  useEffect(() => {
    if (["/target-achievement", "/data-input"].includes(currentPath)) {
      const selectedTab = tabs?.find((tab: any) => tab?.path === currentPath);
      navigate(selectedTab?.path, {
        state: {
          data:
            targetOptions?.allTabs?.find(
              (tab: any) => tab.categories === selectedTab.label
            ) || selectedOption,
          allTabsData: targetOptions?.allTabs,
        },
      });
    }
  }, [selectedOption , currentPath]);

  const searchOptions = React.useMemo(() => {
    return tabs.map((tab: any) => ({
      label: tab.label,
      path: tab.path,
      ...(tab.options && {
        children: tab.optionList?.allTabs?.map((opt: any) => ({
          label: opt.Label,
          path: opt.path,
        })),
      }),
    }));
  }, [tabs]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    setValue(newValue);

    // Pass data to the route, including the selected option
    navigate(selectedTab.path, {
      state: {
        data:
          targetOptions?.allTabs?.find(
            (tab: any) => tab.categories === selectedTab.label
          ) || selectedOption,
      },
    });
    setValue(newValue);
  };

  const handleSearchSelect = (data: any) => {
    const value = data?.target?.value;

    if (value) {
      setSelectedOption(value);
      const selectedTab = tabs.find((tab: any) => tab.path === value.path);

      navigate(value.path, {
        state: {
          data: value || targetOptions?.allTabs[0],
          allTabsData: targetOptions?.allTabs,
        },
      });

      const tabIndex = tabs.findIndex((tab: any) => tab.path === value.path);

      if (tabIndex !== -1) {
        setValue(tabIndex);
      }
    }
  };

  return (
    <Box
      sx={{ bgcolor: "rgb(255, 255, 255)", borderBottom: "1px solid #D0D0D0" }}
    >
      {tabs.length !== 1 && (
        <Tabs
          value={value}
          onChange={handleChange}
          variant={isMediumScreen ? "fullWidth" : "scrollable"}
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          TabIndicatorProps={{
            sx: tabIndicatorStyles,
          }}
        >
          {tabs.map((tab: any, index: any) =>
            tab?.canRead ? (
              tab?.options ? (
                tab?.optionList?.length > 0 ? (
                  <Tab
                    key={index}
                    label={
                      <Dropdown
                        options={
                          Array.isArray(tab.optionList) ? tab.optionList : []
                        }
                        handleChange={handleSearchSelect}
                        selectedValue={{ tabName: tab?.lable }}
                        sx={{
                          backgroundColor: "transparent",
                          flex: 1,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderWidth: "0px !important",
                            borderColor: "transparent !important",
                          },
                        }}
                        size="small"
                        inputLabelSx={{}}
                        placeholder={tab?.label}
                        defaultValue={tab?.label}
                      />
                    }
                    sx={{
                      padding: "3px 7px",
                      minWidth: "200px",
                      "&.Mui-selected": {
                        fontWeight: 700,
                        color: "#242424",
                        backgroundColor: "#FEF7DA",
                      },
                    }}
                  />
                ) : null
              ) : (
                <Tab
                  key={index}
                  label={tab.label}
                  sx={{
                    padding: "3px 7px",
                    textTransform: "capitalize",
                    color: "#242424",
                    "&.Mui-selected": {
                      fontWeight: 700,
                      color: "#242424",
                      backgroundColor: "#FEF7DA",
                    },
                  }}
                />
              )
            ) : null
          )}
        </Tabs>
      )}
    </Box>
  );
};

export default NavigationBar;
